import { matchPath } from "react-router";

// Portal URL shapes and the query keys that travel on them, kept free of React so
// non-component modules — the Auth0 config among them — can build a path without
// pulling in a hooks module.

const portalAuthPrefix = "/portal/auth";
const portalSlugPattern = "/portal/:partnerSlug";

// These three carry no partner slug. `callback` cannot: the identity provider
// validates it against a fixed allowlist that accepts placeholders only in the
// host, never the path, so the partner travels as a query parameter instead.
// `login` and `signup` are slug-free by convention rather than necessity — they
// sit alongside `callback` so one prefix covers the whole auth round trip.
export const portalAuthPaths = {
  login: `${portalAuthPrefix}/login`,
  signup: `${portalAuthPrefix}/signup`,
  callback: `${portalAuthPrefix}/callback`,
} as const;

// Query keys the portal both writes and reads back. Named here so a writer and a
// reader cannot drift apart.
export const PORTAL_QUERY_PARAMS = {
  partnerSlug: "partnerSlug",
  screen: "screen",
  email: "email",
} as const;

// The value of the `screen` key that asks for the signup form.
export const SIGNUP_SCREEN = "signup";

// Ordered for <Switch> and matchPath, which both take the first pattern that
// matches: the literal auth segment has to be tried before the slug pattern, or
// "auth" is read as a partner slug and the app looks up a partner by that name.
export const portalRoutePaths = [portalAuthPrefix, portalSlugPattern] as const;

// A partner's own portal address — the canonical one, and the only shape that
// carries the partner in the path.
export const portalHomePath = (partnerSlug: string) => `/portal/${partnerSlug}`;

// True only for a partner's own address, not for a page nested beneath it.
export const isPortalHomePath = (pathname: string) =>
  Boolean(matchPath(pathname, { path: portalSlugPattern, exact: true }));

export const isPortalAuthPath = (pathname: string) =>
  Object.values(portalAuthPaths).some((authPath) => authPath === pathname);

// The auth paths that may be exchanged for a partner's own address. The post-login
// return address is absent on purpose: the credential exchange delivers its tokens
// in that URL's fragment, so it is only safe to leave once nothing is in flight.
export const isExchangeablePortalAuthPath = (pathname: string) =>
  pathname === portalAuthPaths.login || pathname === portalAuthPaths.signup;

// The sign-in return address, where a "no session" answer from the identity
// provider is provisional rather than final while the exchange completes.
export const isPortalCallbackPath = (pathname: string) =>
  pathname === portalAuthPaths.callback;

// Appends the partner only when there is one. Interpolating an absent slug emits
// the literal string "undefined", which reads back off the query string as a
// truthy partner name and so beats the storage fallback that the auth paths — the
// only paths with no slug segment — depend on.
export const withPartnerSlug = (
  path: string,
  partnerSlug: string | undefined
) =>
  partnerSlug
    ? `${path}?${PORTAL_QUERY_PARAMS.partnerSlug}=${partnerSlug}`
    : path;

// The portal's sign-in address, carrying whatever the caller can supply: the
// partner, so the login page need not fall back to storage, and an address the
// visitor has already given, so they do not type it twice. Absent values are
// omitted rather than interpolated, for the reason `withPartnerSlug` gives.
export const portalLoginHref = (args: {
  partnerSlug?: string;
  email?: string;
}) => {
  const params = new URLSearchParams();

  if (args.partnerSlug) {
    params.set(PORTAL_QUERY_PARAMS.partnerSlug, args.partnerSlug);
  }
  if (args.email) {
    params.set(PORTAL_QUERY_PARAMS.email, args.email);
  }

  const search = params.toString();

  return search ? `${portalAuthPaths.login}?${search}` : portalAuthPaths.login;
};
